import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch, store } from "../../store";
import {
  selectCurrentMap,
  selectMapsWithStats,
  selectMapsLoading,
  selectMapsError,
  fetchMaps,
  createMap,
  updateMap,
  duplicateMap,
  deleteMap,
  setCurrentMap,
  setViewMode,
  selectViewMode,
} from "../../store/mapsSlice";
import { IMap } from "../../types";

const MapManager: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { language } = useSelector((state: RootState) => state.ui);
  const currentMap = useSelector(selectCurrentMap);
  const mapsWithStats = useSelector(selectMapsWithStats);
  const loading = useSelector(selectMapsLoading);
  const error = useSelector(selectMapsError);
  const viewMode = useSelector(selectViewMode);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMapForEdit, setSelectedMapForEdit] = useState<IMap | null>(
    null
  );
  const [newMapData, setNewMapData] = useState({
    name: "",
    description: "",
    buildingName: "",
    floor: "",
    isPublic: false,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editMapData, setEditMapData] = useState({
    name: "",
    description: "",
    buildingName: "",
    floor: "",
    isPublic: false,
  });

  useEffect(() => {
    // Load maps when component mounts, but check if already loaded first
    const loadMaps = async () => {
      const currentState = store.getState();
      if (currentState.maps.loaded || currentState.maps.loading) {
        return; // Skip if already loaded or currently loading
      }
      dispatch(fetchMaps());
    };

    loadMaps();
  }, [dispatch]);

  useEffect(() => {
    // Populate edit form when a map is selected for editing
    if (selectedMapForEdit) {
      setEditMapData({
        name: selectedMapForEdit.name,
        description: selectedMapForEdit.description || "",
        buildingName: selectedMapForEdit.metadata?.buildingName || "",
        floor: selectedMapForEdit.metadata?.floor || "",
        isPublic: selectedMapForEdit.isPublic,
      });
    }
  }, [selectedMapForEdit]);

  const handleCreateMap = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Create the map first
      const resultAction = await dispatch(
        createMap({
          name: newMapData.name,
          description: newMapData.description,
          isPublic: newMapData.isPublic,
          metadata: {
            buildingName: newMapData.buildingName,
            floor: newMapData.floor,
          },
          settings: {
            imageSize: {
              width: 1200,
              height: 800,
            },
            defaultCenter: {
              x: 600,
              y: 400,
            },
            defaultZoom: 1,
            minZoom: 0.1,
            maxZoom: 15,
          },
        })
      );

      if (createMap.fulfilled.match(resultAction)) {
        const response = resultAction.payload;

        // If an image was selected, upload it
        if (selectedImage && response.success && response.map?._id) {
          await handleImageUpload(response.map._id);
        }
      }

      setShowCreateModal(false);
      resetModalState();
    } catch (error) {
      console.error("Failed to create map:", error);
    }
  };

  const handleImageUpload = async (mapId: string) => {
    if (!selectedImage) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("image", selectedImage);

      const response = await fetch(
        `${
          process.env.REACT_APP_BASE_URL || "http://localhost:3051"
        }/api/maps/${mapId}/upload-image`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const result = await response.json();
      console.log("Image uploaded successfully:", result);

      // Refresh maps to get updated data
      dispatch(fetchMaps());
    } catch (error) {
      console.error("Failed to upload image:", error);
    } finally {
      setUploadingImage(false);
    }
  };

  const resetModalState = () => {
    setNewMapData({
      name: "",
      description: "",
      buildingName: "",
      floor: "",
      isPublic: false,
    });
    setEditMapData({
      name: "",
      description: "",
      buildingName: "",
      floor: "",
      isPublic: false,
    });
    setSelectedImage(null);
    setImagePreview(null);
    setUploadingImage(false);
  };

  const handleEditMap = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMapForEdit) return;

    try {
      // Prepare update data - preserve existing settings and metadata, only update what's changed
      const updateData = {
        name: editMapData.name,
        description: editMapData.description,
        isPublic: editMapData.isPublic,
        metadata: {
          ...selectedMapForEdit.metadata, // Preserve existing metadata
          buildingName: editMapData.buildingName,
          floor: editMapData.floor,
        },
        settings: {
          ...selectedMapForEdit.settings, // Preserve existing settings
        },
      };

      // Update the map first
      const resultAction = await dispatch(
        updateMap({
          mapId: selectedMapForEdit._id,
          updates: updateData,
        })
      );

      if (updateMap.fulfilled.match(resultAction)) {
        // If a new image was selected, upload it
        if (selectedImage) {
          await handleImageUpload(selectedMapForEdit._id);
        }
      }

      setShowEditModal(false);
      setSelectedMapForEdit(null);
      resetModalState();
    } catch (error) {
      console.error("Failed to update map:", error);
    }
  };

  const handleRemoveCurrentImage = async () => {
    if (!selectedMapForEdit) return;

    try {
      setUploadingImage(true);
      const response = await fetch(
        `/api/maps/${selectedMapForEdit._id}/image`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to remove image");
      }

      console.log("Image removed successfully");

      // Refresh maps to get updated data
      dispatch(fetchMaps());
    } catch (error) {
      console.error("Failed to remove image:", error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert(
          language === "he"
            ? "יש לבחור קובץ תמונה"
            : "Please select an image file"
        );
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert(
          language === "he"
            ? "קובץ התמונה גדול מדי (מקסימום 10MB)"
            : "Image file is too large (max 10MB)"
        );
        return;
      }

      setSelectedImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (imageFile) {
      // Validate file size (10MB limit)
      if (imageFile.size > 10 * 1024 * 1024) {
        alert(
          language === "he"
            ? "קובץ התמונה גדול מדי (מקסימום 10MB)"
            : "Image file is too large (max 10MB)"
        );
        return;
      }

      setSelectedImage(imageFile);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(imageFile);
    } else {
      alert(
        language === "he"
          ? "יש לבחור קובץ תמונה"
          : "Please select an image file"
      );
    }
  };

  const handleDuplicateMap = async (map: IMap) => {
    try {
      await dispatch(duplicateMap(map._id)).unwrap();
    } catch (error) {
      console.error("Failed to duplicate map:", error);
    }
  };

  const handleDeleteMap = async (map: IMap) => {
    if (
      window.confirm(
        language === "he"
          ? `האם אתה בטוח שברצונך למחוק את המפה "${map.name}"?`
          : `Are you sure you want to delete the map "${map.name}"?`
      )
    ) {
      try {
        await dispatch(deleteMap(map._id)).unwrap();
      } catch (error) {
        console.error("Failed to delete map:", error);
      }
    }
  };

  const handleSwitchMap = (mapId: string) => {
    dispatch(setCurrentMap(mapId));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      language === "he" ? "he-IL" : "en-US",
      { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {language === "he" ? "ניהול מפות" : "Map Management"}
            </h3>
            <p className="text-sm text-gray-500">
              {language === "he"
                ? `${mapsWithStats.totalMaps} מפות`
                : `${mapsWithStats.totalMaps} maps`}
              {/* {language === "he"
                  ? `${mapsWithStats.totalMaps} מפות (${mapsWithStats.userOwnedMaps} שלי, ${mapsWithStats.sharedMaps} משותפות)`
                  : `${mapsWithStats.totalMaps} maps (${mapsWithStats.userOwnedMaps} owned, ${mapsWithStats.sharedMaps} shared)`} */}
            </p>
          </div>

          <button
            onClick={() =>
              dispatch(setViewMode(viewMode === "tabs" ? "grid" : "tabs"))
            }
            className="btn-secondary text-sm"
            title={language === "he" ? "החלף תצוגה" : "Toggle view mode"}
          >
            {viewMode === "tabs" ? (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h7"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Current Map Info */}
      {currentMap && (
        <div className="p-4 bg-blue-50 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-blue-900 truncate">
                {language === "he" ? "מפה נוכחית:" : "Current Map:"}{" "}
                {currentMap.name}
              </h4>
              <div className="flex items-center mt-1 text-xs text-blue-700">
                {/* <span className="mr-4">
                                    {currentMap.accessLevel === 'owner' 
                                        ? (language === 'he' ? 'בעלים' : 'Owner')
                                        : (language === 'he' ? `משותף (${currentMap.accessLevel})` : `Shared (${currentMap.accessLevel})`)
                                    }
                                </span> */}
                {/* {currentMap.accessCount > 0 && (
                    <span>
                      {currentMap.accessCount}{" "}
                      {language === "he" ? "צפיות" : "views"}
                    </span>
                  )} */}
              </div>
            </div>

            <div className="flex items-center space-x-1 rtl:space-x-reverse">
              {/* <button
                  onClick={() => handleDuplicateMap(currentMap)}
                  className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                  title={language === "he" ? "שכפל מפה" : "Duplicate map"}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button> */}

              {currentMap.accessLevel === "owner" && (
                <button
                  onClick={() => handleDeleteMap(currentMap)}
                  className="p-1 text-red-600 hover:text-red-800 transition-colors"
                  title={language === "he" ? "מחק מפה" : "Delete map"}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">
                {language === "he" ? "טוען..." : "Loading..."}
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-red-400 mb-2">
                <svg
                  className="w-8 h-8 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-sm text-gray-500">
                {language === "he"
                  ? "שגיאה בטעינת המפות"
                  : "Error loading maps"}
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4 flex-1">
            {/* Create New Map Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full mb-4 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-300 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              {language === "he" ? "צור מפה חדשה" : "Create New Map"}
            </button>

            {/* Maps List */}
            <div className="space-y-3">
              {mapsWithStats.maps.map((map) => (
                <div
                  key={map._id}
                  onClick={() => handleSwitchMap(map._id)}
                  className={`
                                        p-3 rounded-lg border cursor-pointer transition-colors duration-200
                                        ${
                                          currentMap?._id === map._id
                                            ? "border-blue-500 bg-blue-50"
                                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                        }
                                    `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {map.name}
                      </h4>

                      {map.description && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {map.description}
                        </p>
                      )}

                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        {/* <span
                          className={`
                                                    px-2 py-1 rounded-full text-xs font-medium mr-2
                                                    ${
                                                      map.accessLevel ===
                                                      "owner"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-blue-100 text-blue-700"
                                                    }
                                                `}
                        >
                          {map.accessLevel === "owner"
                            ? language === "he"
                              ? "שלי"
                              : "Owned"
                            : language === "he"
                            ? "משותף"
                            : "Shared"}
                        </span> */}

                        {/* {map.lastAccessedAt && (
                          <span className="mr-2">
                            {formatDate(map.lastAccessedAt)}
                          </span>
                        )} */}

                        {map?.metadata?.buildingName && (
                          <span className="">
                            <span>
                              {language === "he" ? "מבנה" : "Building"}:{" "}
                            </span>
                            {map?.metadata?.buildingName}
                          </span>
                        )}
                        {map?.metadata?.floor && (
                          <span className="mr-2">
                            <span>
                              {map?.metadata?.buildingName ? ", " : " "}
                            </span>
                            <span>
                              {language === "he" ? "קומה" : "floor"}:{" "}
                            </span>
                            {map?.metadata?.floor}
                          </span>
                        )}

                        {/* <span className="flex items-center">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {map.accessCount || 0}
                        </span> */}
                      </div>
                    </div>

                    <div className="flex items-center ml-2 space-x-1">
                      {map.accessLevel === "owner" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMapForEdit(map);
                            setShowEditModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title={language === "he" ? "ערוך מפה" : "Edit map"}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                      )}
                      {currentMap?._id === map._id && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {mapsWithStats.maps.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <svg
                      className="w-12 h-12 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500">
                    {language === "he" ? "אין מפות עדיין" : "No maps yet"}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Map Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => {
            setShowCreateModal(false);
            resetModalState();
          }}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium mb-4">
              {language === "he" ? "צור מפה חדשה" : "Create New Map"}
            </h3>

            <form onSubmit={handleCreateMap}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "שם המפה" : "Map Name"} *
                  </label>
                  <input
                    type="text"
                    value={newMapData.name}
                    onChange={(e) =>
                      setNewMapData({ ...newMapData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    placeholder={
                      language === "he" ? "הכנס שם למפה" : "Enter map name"
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "תיאור" : "Description"}
                  </label>
                  <textarea
                    value={newMapData.description}
                    onChange={(e) =>
                      setNewMapData({
                        ...newMapData,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder={
                      language === "he"
                        ? "תיאור אופציונלי"
                        : "Optional description"
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "שם הבניין" : "Building Name"}
                  </label>
                  <input
                    type="text"
                    value={newMapData.buildingName}
                    onChange={(e) =>
                      setNewMapData({
                        ...newMapData,
                        buildingName: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={
                      language === "he" ? "שם בניין" : "Building name"
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "קומה" : "Floor"}
                  </label>
                  <input
                    type="text"
                    value={newMapData.floor}
                    onChange={(e) =>
                      setNewMapData({ ...newMapData, floor: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={
                      language === "he" ? "מספר קומה" : "Floor number"
                    }
                  />
                </div>

                {/* Background Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === "he"
                      ? "תמונת רקע למפה"
                      : "Map Background Image"}
                  </label>

                  {!imagePreview ? (
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg
                            className="w-8 h-8 mb-2 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <p className="text-xs text-gray-500">
                            {language === "he"
                              ? "לחץ לבחירת תמונה או גרור ושחרר כאן"
                              : "Click to upload or drag and drop"}
                          </p>
                          <p className="text-xs text-gray-400">
                            PNG, JPG, JPEG, GIF, WebP (Max 10MB)
                          </p>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="w-full h-32 rounded-lg overflow-hidden border border-gray-300">
                        <img
                          src={imagePreview}
                          alt="New map background preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                        title={language === "he" ? "הסר תמונה" : "Remove image"}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-1">
                    {language === "he"
                      ? "תמונה אופציונלית לרקע המפה. ניתן להוסיף גם מאוחר יותר."
                      : "Optional background image for the map. You can add one later too."}
                  </p>
                </div>

                {/* <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newMapData.isPublic}
                        onChange={(e) =>
                          setNewMapData({
                            ...newMapData,
                            isPublic: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {language === "he" ? "מפה ציבורית" : "Public map"}
                      </span>
                    </label>
                  </div> */}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetModalState();
                  }}
                  className="btn-secondary"
                  disabled={uploadingImage}
                >
                  {language === "he" ? "ביטול" : "Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={uploadingImage}
                  className="btn-primary flex items-center"
                >
                  {uploadingImage ? (
                    <>
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                      {language === "he"
                        ? "מעלה תמונה..."
                        : "Uploading image..."}
                    </>
                  ) : language === "he" ? (
                    "צור מפה"
                  ) : (
                    "Create Map"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Map Modal */}
      {showEditModal && selectedMapForEdit && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => {
            setShowEditModal(false);
            setSelectedMapForEdit(null);
            resetModalState();
          }}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium mb-4">
              {language === "he" ? "ערוך מפה" : "Edit Map"}
            </h3>

            <form onSubmit={handleEditMap}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "שם המפה" : "Map Name"} *
                  </label>
                  <input
                    type="text"
                    value={editMapData.name}
                    onChange={(e) =>
                      setEditMapData({ ...editMapData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    placeholder={
                      language === "he" ? "הכנס שם למפה" : "Enter map name"
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "תיאור" : "Description"}
                  </label>
                  <textarea
                    value={editMapData.description}
                    onChange={(e) =>
                      setEditMapData({
                        ...editMapData,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder={
                      language === "he"
                        ? "תיאור אופציונלי"
                        : "Optional description"
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "שם הבניין" : "Building Name"}
                  </label>
                  <input
                    type="text"
                    value={editMapData.buildingName}
                    onChange={(e) =>
                      setEditMapData({
                        ...editMapData,
                        buildingName: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={
                      language === "he" ? "שם בניין" : "Building name"
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "קומה" : "Floor"}
                  </label>
                  <input
                    type="text"
                    value={editMapData.floor}
                    onChange={(e) =>
                      setEditMapData({
                        ...editMapData,
                        floor: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={
                      language === "he" ? "מספר קומה" : "Floor number"
                    }
                  />
                </div>

                {/* Current Background Image */}
                {selectedMapForEdit.settings?.backgroundImage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === "he"
                        ? "תמונת רקע נוכחית"
                        : "Current Background Image"}
                    </label>
                    <div className="relative">
                      <div className="w-full h-32 rounded-lg overflow-hidden border border-gray-300">
                        <img
                          src={
                            selectedMapForEdit?.settings?.backgroundImage.startsWith(
                              "/images/"
                            )
                              ? `${
                                  process.env.REACT_APP_BASE_URL ||
                                  "http://localhost:3051"
                                }${
                                  selectedMapForEdit.settings.backgroundImage
                                }?token=${localStorage.getItem("token")}`
                              : selectedMapForEdit?.settings?.backgroundImage.startsWith(
                                  "/uploads/"
                                )
                              ? `${
                                  process.env.REACT_APP_BASE_URL ||
                                  "http://localhost:3051"
                                }${selectedMapForEdit.settings.backgroundImage}`
                              : selectedMapForEdit?.settings?.backgroundImage
                          }
                          alt="Current map background"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveCurrentImage}
                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                        title={
                          language === "he"
                            ? "הסר תמונה נוכחית"
                            : "Remove current image"
                        }
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* New Background Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === "he"
                      ? "תמונת רקע חדשה"
                      : "New Background Image"}
                  </label>

                  {!imagePreview ? (
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="edit-image-upload"
                      />
                      <label
                        htmlFor="edit-image-upload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg
                            className="w-8 h-8 mb-2 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <p className="text-xs text-gray-500">
                            {language === "he"
                              ? "לחץ לבחירת תמונה או גרור ושחרר כאן"
                              : "Click to upload or drag and drop"}
                          </p>
                          <p className="text-xs text-gray-400">
                            PNG, JPG, JPEG, GIF, WebP (Max 10MB)
                          </p>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="w-full h-32 rounded-lg overflow-hidden border border-gray-300">
                        <img
                          src={imagePreview}
                          alt="New map background preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                          title={
                            language === "he" ? "הסר תמונה" : "Remove image"
                          }
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-1">
                    {language === "he"
                      ? "תמונה אופציונלית לרקע המפה. השאר ריק כדי לשמור על התמונה הנוכחית."
                      : "Optional new background image. Leave empty to keep current image."}
                  </p>
                </div>

                {/* <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editMapData.isPublic}
                        onChange={(e) =>
                          setEditMapData({
                            ...editMapData,
                            isPublic: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {language === "he" ? "מפה ציבורית" : "Public map"}
                      </span>
                    </label>
                  </div> */}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedMapForEdit(null);
                    resetModalState();
                  }}
                  className="btn-secondary"
                  disabled={uploadingImage}
                >
                  {language === "he" ? "ביטול" : "Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={uploadingImage}
                  className="btn-primary flex items-center"
                >
                  {uploadingImage ? (
                    <>
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                      {language === "he"
                        ? "מעלה תמונה..."
                        : "Uploading image..."}
                    </>
                  ) : language === "he" ? (
                    "שמור שינויים"
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapManager;
